import {createSlice, isAnyOf, PayloadAction} from '@reduxjs/toolkit';
import {THUNK_STATUS, AudioUpload} from '../../domain/types';
import {ApiError} from '../../lib/apiError';
import {UploadProgress} from '../../lib/r2Upload';
import {
  fetchAudioUploads,
  uploadAudioFile,
  resumeAudioUpload,
  deleteAudioUpload,
} from './audioThunks';

/**
 * Where an in-flight upload has got to. Only `uploading` has a meaningful percentage — the other
 * phases are short round-trips, and showing a bar that sits at 0% through two of them reads as a
 * stall.
 *
 * `resuming` is the `/parts` round-trip that asks R2 what it already holds. It gets its own phase
 * rather than being folded into `requesting` because the wording differs in the way that matters:
 * nothing is being prepared, something is being checked, and on a mostly-finished upload the bar
 * that appears afterwards starts nearly full.
 */
export type UploadPhase = 'idle' | 'requesting' | 'resuming' | 'uploading' | 'finalizing';

interface AudioState {
  api: {
    status: THUNK_STATUS;
  };
  data: AudioUpload[];
  /** Detail state: the upload the user last acted on — usually the one just uploaded. */
  selectedAudioId: string | null;
  /**
   * Bumped when something outside the workspace asks for the file picker — the header's primary
   * "Upload audio" button. It is a counter rather than a boolean so two clicks in a row both land,
   * and the workspace opens the picker on every change rather than on a truthy value it would then
   * have to reset.
   */
  uploadRequestId: number;
  upload: {
    status: THUNK_STATUS;
    phase: UploadPhase;
    progress: UploadProgress;
    error: ApiError | null;
    /**
     * The row the bytes are going into, known from the moment `/upload-url` answers rather than
     * only when the upload finishes. Uploads are resumable and therefore visible in the file list
     * while they run, so the list needs to know which row is the live one — otherwise a `pending`
     * row would offer Resume for an upload already in flight.
     */
    audioId: string | null;
  };
}

const idleProgress: UploadProgress = {loaded: 0, total: 0, percent: 0};

const initialState: AudioState = {
  api: {status: THUNK_STATUS.idle},
  data: [],
  selectedAudioId: null,
  uploadRequestId: 0,
  upload: {
    status: THUNK_STATUS.idle,
    phase: 'idle',
    progress: idleProgress,
    error: null,
    audioId: null,
  },
};

const audioSlice = createSlice({
  name: 'audio',
  initialState,
  reducers: {
    setSelectedAudioId: (state, action: PayloadAction<string | null>) => {
      state.selectedAudioId = action.payload;
    },
    requestAudioUpload: (state) => {
      state.uploadRequestId += 1;
    },
    setUploadPhase: (state, action: PayloadAction<UploadPhase>) => {
      state.upload.phase = action.payload;
    },
    setUploadProgress: (state, action: PayloadAction<UploadProgress>) => {
      state.upload.progress = action.payload;
    },
    setUploadingAudioId: (state, action: PayloadAction<string | null>) => {
      state.upload.audioId = action.payload;
    },
    clearUploadError: (state) => {
      state.upload.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAudioUploads.pending, (state) => {
      state.api.status = THUNK_STATUS.pending;
    });
    builder.addCase(fetchAudioUploads.fulfilled, (state, action) => {
      state.api.status = THUNK_STATUS.fulfilled;
      state.data = action.payload;

      // A selection that no longer exists (deleted elsewhere, or a project switch) would leave the
      // transcribe form pointing at nothing.
      if (state.selectedAudioId && !action.payload.some(a => a.id === state.selectedAudioId)) {
        state.selectedAudioId = null;
      }
    });
    builder.addCase(fetchAudioUploads.rejected, (state) => {
      state.api.status = THUNK_STATUS.rejected;
    });

    builder.addCase(deleteAudioUpload.fulfilled, (state, action) => {
      state.data = state.data.filter(a => a.id !== action.payload);
      if (state.selectedAudioId === action.payload) {
        state.selectedAudioId = null;
      }
    });

    // A fresh upload and a resumed one end in exactly the same place — a `ready` record that belongs
    // at the top of the list and selected — so the two thunks share their reducers rather than
    // keeping two copies that could drift.
    builder.addMatcher(
      isAnyOf(uploadAudioFile.pending, resumeAudioUpload.pending),
      (state) => {
        state.upload.status = THUNK_STATUS.pending;
        state.upload.error = null;
        state.upload.progress = idleProgress;
      }
    );
    builder.addMatcher(
      isAnyOf(uploadAudioFile.fulfilled, resumeAudioUpload.fulfilled),
      (state, action: PayloadAction<AudioUpload>) => {
        state.upload.status = THUNK_STATUS.fulfilled;
        state.upload.phase = 'idle';
        state.upload.progress = idleProgress;
        state.upload.audioId = null;

        state.data = [action.payload, ...state.data.filter(a => a.id !== action.payload.id)];
        // Pre-select it as the row just acted on. Not an assumption that a transcription follows —
        // plenty of uploads are the end of the story — only that this is the file in hand.
        state.selectedAudioId = action.payload.id;
      }
    );
    builder.addMatcher(
      isAnyOf(uploadAudioFile.rejected, resumeAudioUpload.rejected),
      (state, action: {payload?: ApiError}) => {
        state.upload.status = THUNK_STATUS.rejected;
        state.upload.phase = 'idle';
        state.upload.progress = idleProgress;
        // The row itself is deliberately left alone: a failed upload is still `pending` on the
        // server and still resumable, so it keeps its place in the list with a Resume button.
        state.upload.audioId = null;
        state.upload.error = action.payload ?? {status: 0, message: 'The upload failed'};
      }
    );

  },
});

export const {
  setSelectedAudioId,
  requestAudioUpload,
  setUploadPhase,
  setUploadProgress,
  setUploadingAudioId,
  clearUploadError,
} = audioSlice.actions;

export default audioSlice.reducer;
